import { useTranslation } from "react-i18next";
import i18n from "@/lib/i18n";
import React, { useMemo, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Trash2, Package, ChevronDown, AlertCircle } from "lucide-react";
import { useCustomParts } from "@/hooks/use-custom-parts";
import type { CustomPart, QuoteCustomPart } from "@onsemble/shared";
import { multiplyMoney } from "@/lib/price-utils";
import { CustomPartSelector } from "./CustomPartSelector";
import { SelectedCustomPartItem } from "./SelectedCustomPartItem";

// Use QuoteCustomPart directly - no transformation layers needed
export type SelectedCustomPart = QuoteCustomPart;

interface SelectedCustomPartsListProps {
  selectedCustomParts: SelectedCustomPart[];
  onCustomPartsChange: (customParts: SelectedCustomPart[]) => void;
  onCustomPartQuantityChange: (customPartId: string, quantity: number) => void;
  onCustomPartRemove: (customPartId: string) => void;
  onClearAll: () => void;
  onTotalChange?: (total: number) => void;
  disabled?: boolean;
  title?: string;
  className?: string;
  orgId: string;
  // For the embedded dropdown selector
  onCustomPartSelect: (customPart: CustomPart) => void;
  onAddNewCustomPart: () => void;
}

export const SelectedCustomPartsList: React.FC<
  SelectedCustomPartsListProps
> = ({
  selectedCustomParts,
  onCustomPartQuantityChange,
  onCustomPartRemove,
  onClearAll,
  onTotalChange,
  disabled = false,
  title = i18n.t("components.customParts.selectedCustomPartsList.selectedCustomParts"),
  className,
  onCustomPartSelect,
  onAddNewCustomPart,
}) => {
  const { t } = useTranslation();
  const { customParts, isLoading: customPartsLoading } = useCustomParts();

  // Calculate summary information - matches products pattern exactly
  const summary = useMemo(() => {
    const totalItems = selectedCustomParts.reduce((sum, customPart) => {
      const quantity = customPart.quantity;
      return sum + quantity;
    }, 0);

    let totalValueCents = 0;
    let hasMissingPrices = false;

    selectedCustomParts.forEach((customPart) => {
      if (customPart.customPrice !== undefined) {
        // customPrice is already in cents, multiply by quantity
        const customPartTotalCents = multiplyMoney(
          customPart.customPrice,
          customPart.quantity,
        );
        totalValueCents += customPartTotalCents;
      } else {
        hasMissingPrices = true;
      }
    });

    const totalValue = totalValueCents / 100; // Convert to dollars like products
    return { totalItems, totalValue, hasMissingPrices };
  }, [selectedCustomParts]);

  // Notify parent of total changes
  React.useEffect(() => {
    if (onTotalChange) {
      onTotalChange(summary.totalValue);
    }
  }, [summary.totalValue, onTotalChange]);

  // Helper to find full custom part details
  const getCustomPartDetails = useCallback(
    (customPartId: string): CustomPart | undefined => {
      return customParts?.find((cp) => cp.uid === customPartId);
    },
    [customParts],
  );

  const handleQuantityChange = useCallback(
    (customPartId: string, quantity: number) => {
      onCustomPartQuantityChange(customPartId, quantity);
    },
    [onCustomPartQuantityChange],
  );

  const handleRemoveCustomPart = useCallback(
    (customPartId: string) => {
      onCustomPartRemove(customPartId);
    },
    [onCustomPartRemove],
  );

  if (selectedCustomParts.length === 0) {
    return (
      <Card className={className}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base font-medium">
              <Package className="h-4 w-4" />
              {title} (0)
            </CardTitle>
            <CustomPartSelector
              customParts={customParts || []}
              selectedCustomParts={[]}
              onCustomPartSelect={onCustomPartSelect}
              onAddNewCustomPart={onAddNewCustomPart}
              disabled={disabled}
            />
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>{t("components.customParts.selectedCustomPartsList.noCustomPartsSelected")}</p>
            <p className="text-sm">
              {t("components.customParts.selectedCustomPartsList.useTheSelectorIn")}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base font-medium">
            <Package className="h-4 w-4" />
            {title} ({selectedCustomParts.length})
          </CardTitle>
          <div className="flex items-center gap-2">
            <CustomPartSelector
              customParts={customParts || []}
              selectedCustomParts={selectedCustomParts.map(
                (cp) => cp.customPartId,
              )}
              onCustomPartSelect={onCustomPartSelect}
              onAddNewCustomPart={onAddNewCustomPart}
              disabled={disabled}
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onClearAll}
              disabled={disabled}
              className="text-muted-foreground hover:text-destructive"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Selected Custom Parts List */}
        <div className="relative">
          {selectedCustomParts.length > 4 && (
            <div className="absolute bottom-2 right-2 text-xs text-white font-semibold bg-slate-800 px-2 py-1 rounded-full shadow-lg z-10 flex items-center gap-1 transition-opacity duration-200">
              <ChevronDown className="h-3 w-3" />
              {t("components.customParts.selectedCustomPartsList.scrollForMore")}
            </div>
          )}
          <div
            className="space-y-3 max-h-[32rem] overflow-auto"
            onScroll={(e) => {
              const target = e.target as HTMLDivElement;
              const { scrollTop, scrollHeight, clientHeight } = target;
              const isAtBottom = scrollTop + clientHeight >= scrollHeight - 5;

              const container = target.parentElement;
              if (container) {
                container.style.setProperty(
                  "--scroll-badge-opacity",
                  isAtBottom ? "0" : "1",
                );
              }
            }}
          >
            {selectedCustomParts.map((selectedCustomPart) => {
              const customPartDetails = getCustomPartDetails(
                selectedCustomPart.customPartId,
              );

              if (!customPartDetails) {
                // If custom parts are still loading, show a loading placeholder instead of error
                if (customPartsLoading) {
                  return (
                    <div
                      key={selectedCustomPart.customPartId}
                      className="p-3 border rounded-lg bg-gray-50"
                    >
                      <div className="flex items-center gap-2 text-gray-500">
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600"></div>
                        <span className="text-sm">
                          {t("components.customParts.selectedCustomPartsList.loadingCustomPartDetails")}
                        </span>
                      </div>
                    </div>
                  );
                }

                // Only show error if custom parts are loaded but custom part is still not found
                return (
                  <div
                    key={selectedCustomPart.customPartId}
                    className="p-3 border rounded-lg bg-red-50"
                  >
                    <div className="flex items-center gap-2 text-red-600">
                      <AlertCircle className="h-4 w-4" />
                      <span className="text-sm">
                        {t("components.customParts.selectedCustomPartsList.customPartNotFound")}{" "}
                        {selectedCustomPart.customPartId})
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          handleRemoveCustomPart(
                            selectedCustomPart.customPartId,
                          )
                        }
                        className="ml-auto text-red-600 hover:text-red-800"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                );
              }

              return (
                <SelectedCustomPartItem
                  key={selectedCustomPart.customPartId}
                  selectedCustomPart={selectedCustomPart}
                  customPart={customPartDetails}
                  onQuantityChange={(quantity) =>
                    handleQuantityChange(
                      selectedCustomPart.customPartId,
                      quantity,
                    )
                  }
                  onRemove={() =>
                    handleRemoveCustomPart(selectedCustomPart.customPartId)
                  }
                  showPricing={true}
                  disabled={disabled}
                />
              );
            })}
          </div>
        </div>

        {/* Summary Section */}
        <Separator className="my-4" />
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">{t("components.customParts.selectedCustomPartsList.totalItems")}</span>
            <span className="font-medium">{summary.totalItems}</span>
          </div>

          {summary.totalValue > 0 && (
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">
                {summary.hasMissingPrices ? t("components.customParts.selectedCustomPartsList.partialTotal") : t("components.customParts.selectedCustomPartsList.totalValue")}
              </span>
              <span className="font-bold text-lg text-primary">
                ${summary.totalValue.toFixed(2)}
                {summary.hasMissingPrices ? "+" : ""}
              </span>
            </div>
          )}

          {summary.totalValue === 0 && (
            <div className="flex items-center gap-2 text-amber-600 bg-amber-50 rounded-lg p-2">
              <AlertCircle className="h-4 w-4" />
              <span className="text-sm">
                {t("components.customParts.selectedCustomPartsList.pricingWillBeCalculated")}
              </span>
            </div>
          )}

          {summary.hasMissingPrices && summary.totalValue > 0 && (
            <div className="flex items-center gap-2 text-amber-600 text-xs">
              <AlertCircle className="h-3 w-3" />
              <span>{t("components.customParts.selectedCustomPartsList.somePricesPendingTotal")}</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
